import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { canManage } from './permissions';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAs, setViewAsState] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [authError, setAuthError] = useState(null);

  const fetchUser = useCallback(async (viewAsUser) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (viewAsUser) headers['X-BidBoard-ViewAs'] = viewAsUser;

      const res = await fetch(`${API_BASE}/auth/me`, {
        headers,
        credentials: 'same-origin',
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data);
        setAuthError(null);
      } else if (res.status === 401) {
        setUser(null);
        setAuthError({ status: 401 });
      } else {
        const body = await res.json().catch(() => ({}));
        setUser(null);
        setAuthError({ status: res.status, error: body.error || res.statusText });
      }
    } catch (err) {
      console.error('Auth error:', err);
      setUser(null);
      setAuthError({ status: 0, error: err.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    function handleSessionExpired() {
      setUser(null);
      setViewAsState(null);
      setAllUsers([]);
      setAuthError({ status: 401 });
    }
    window.addEventListener('bidboard-session-expired', handleSessionExpired);
    return () => window.removeEventListener('bidboard-session-expired', handleSessionExpired);
  }, []);

  useEffect(() => {
    if (canManage(user)) {
      fetch(`${API_BASE}/auth/users`, { credentials: 'same-origin' })
        .then(r => r.ok ? r.json() : [])
        .then(setAllUsers)
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isAdmin, user?.permission]);

  function handleLogin(userData) {
    setUser(userData);
    setAuthError(null);
    setLoading(false);
  }

  function setViewAs(username) {
    setViewAsState(username);
    setLoading(true);
    fetchUser(username);
  }

  function clearViewAs() {
    setViewAsState(null);
    setLoading(true);
    fetchUser(null);
  }

  async function logout() {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'same-origin',
      });
    } catch (e) {}
    setUser(null);
    setViewAsState(null);
    setAllUsers([]);
    setAuthError({ status: 401 });
  }

  function getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    if (viewAs) headers['X-BidBoard-ViewAs'] = viewAs;
    return headers;
  }

  return (
    <AuthContext.Provider value={{
      user, loading, viewAs, setViewAs, clearViewAs,
      allUsers, getHeaders, authError,
      handleLogin, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
